import React from 'react';


const stableHash = (value: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

export const deterministicAssessmentShuffle = (items: string[], seed: string): string[] => (
  [...items]
    .map((item, index) => ({ item, index, key: stableHash(`${seed}|${item}|${index}`) }))
    .sort((left, right) => (left.key - right.key) || (left.index - right.index))
    .map((entry) => entry.item)
);

export const OrderingResponseEditor: React.FC<{
  items: string[];
  disabled?: boolean;
  onChange: (items: string[]) => void;
  emptyMessage?: string;
}> = ({ items, disabled = false, onChange, emptyMessage = 'No ordering items are configured.' }) => (
  <div className="space-y-2 rounded border border-blue-200 bg-white p-3">
    <p className="text-xs uppercase tracking-[0.2em] text-blue-700">Ordering</p>
    {items.length === 0 && <p className="text-sm text-gray-600">{emptyMessage}</p>}
    {items.map((item, index) => (
      <div key={`${item}-${index}`} className="flex items-center justify-between gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2">
        <span className="text-sm text-gray-800">{item || `Item ${index + 1}`}</span>
        <div className="flex items-center gap-1">
          <button type="button" aria-label={`Move ${item} up`} className="rounded border px-2 py-1 text-xs disabled:opacity-50" disabled={disabled || index === 0} onClick={() => { const next = [...items]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; onChange(next); }}>↑</button>
          <button type="button" aria-label={`Move ${item} down`} className="rounded border px-2 py-1 text-xs disabled:opacity-50" disabled={disabled || index === items.length - 1} onClick={() => { const next = [...items]; [next[index + 1], next[index]] = [next[index], next[index + 1]]; onChange(next); }}>↓</button>
        </div>
      </div>
    ))}
  </div>
);

export const MatchingResponseEditor: React.FC<{
  leftItems: string[];
  rightItems: string[];
  value: Record<string, string>;
  seed: string;
  disabled?: boolean;
  onChange: (value: Record<string, string>) => void;
  emptyMessage?: string;
}> = ({ leftItems, rightItems, value, seed, disabled = false, onChange, emptyMessage = 'Matching items are not configured.' }) => (
  <div className="space-y-3 rounded border border-blue-200 bg-white p-3">
    <p className="text-xs uppercase tracking-[0.2em] text-blue-700">Matching</p>
    {leftItems.length === 0 || rightItems.length === 0 ? (
      <p className="text-sm text-gray-600">{emptyMessage}</p>
    ) : leftItems.map((leftItem) => (
      <div key={leftItem} className="grid grid-cols-1 gap-2 md:grid-cols-2 md:items-center">
        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">{leftItem}</div>
        <select className="w-full rounded border border-gray-300 px-3 py-2 text-sm" value={value[leftItem] || ''} onChange={(event) => onChange({ ...value, [leftItem]: event.target.value })} disabled={disabled} aria-label={`Match for ${leftItem}`}>
          <option value="">Select a match</option>
          {deterministicAssessmentShuffle(rightItems, `${seed}:${leftItem}`).map((rightItem) => <option key={`${leftItem}-${rightItem}`} value={rightItem}>{rightItem}</option>)}
        </select>
      </div>
    ))}
  </div>
);
